import { useState } from 'react';
import PageLayout from '../components/global/PageLayout';
import PageLoading from '../components/global/PageLoading';
import EmptyQueue from '../components/queue/EmptyQueue';
import QueueError from '../components/queue/QueueError';
import OfflineState from '../components/global/OfflineState';
import QueueTable from '../components/queue/QueueTable';
import { userHasAccessToQueue } from '../helpers/queue';
import useUser from '../hooks/global/useUser';
import useGetQueue from '../hooks/queue/useGetQueue';
import FullPageModal from '../components/modal/FullPageModal';
import AppointmentForm from '../components/form/AppointmentForm';
import { QueueAppointment } from '../types/queue';
import { Appointment } from '../interfaces';
import AppointmentSideBarModal from '../components/appointments/AppointmentSideBarModal';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import useCheckoutForm from '../hooks/global/useCheckoutForm';
import useCheckoutSocket from '../hooks/payments/useCheckoutSocket';
import useCheckPendingPayment from '../hooks/payments/useCheckPendingPayment';
import useInSplitCheckout from '../hooks/payments/useInSplitCheckout';
import { getCheckoutErrorMessage } from '../helpers/payments';
import { invalidateQueue } from '../services/queue';
import { invalidateDepositTransferAppointments } from '../services/appointments';
import {
  invalidateCustomerGiftCardBalances,
  invalidateGiftCardsPage,
} from '../services/gift-cards';

export default function QueuePage() {
  const { user } = useUser();
  const {
    queue,
    isLoading,
    isFetching,
    isError,
    isPaused,
    isRefetching,
    refetch: refetchQueue,
  } = useGetQueue();

  const [isNewAppointmentFormOpen, setIsNewAppointmentFormOpen] =
    useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<
    Appointment | QueueAppointment | null
  >(null);
  const [selectedViewAppointmentId, setSelectedViewAppointmentId] = useState<
    string | null
  >(null);

  const { openSubscriptionModal } = useSubscriptionModal();

  const hasQueue = useFeatureGuard(Feature.ADD_APPOINTMENT);
  const hasQueueData = queue && queue.length > 0;

  const {
    terminalCheckoutModal,
    resetTerminalCheckoutModalState,
    setTerminalCheckoutModalToSuccess,
    setCheckoutFailureData,
    setTerminalCheckoutModalToFailed,
    mostRecentPaymentId,
  } = useCheckoutForm();

  const isInSplitCheckout = useInSplitCheckout();

  useCheckPendingPayment({
    enabled: !isInSplitCheckout,
  });
  useCheckoutSocket({
    enabled: !isInSplitCheckout,
    onCheckoutCompleted: async data => {
      if (!data.paymentId || data.paymentId !== mostRecentPaymentId) return;
      invalidateQueue();

      if (data.appointmentId) {
        invalidateDepositTransferAppointments();
      }

      if (terminalCheckoutModal.status === 'loading') {
        setTerminalCheckoutModalToSuccess({
          paymentId: data.paymentId,
          isGuest: !data.customerId,
        });
      } else {
        resetTerminalCheckoutModalState();
      }

      invalidateCustomerGiftCardBalances();
      invalidateGiftCardsPage();
    },
    onCheckoutFailed: async data => {
      if (!data.paymentId || data.paymentId !== mostRecentPaymentId) return;

      setCheckoutFailureData(
        data as Parameters<typeof setCheckoutFailureData>[0],
      );
      setTerminalCheckoutModalToFailed(getCheckoutErrorMessage(data));
      invalidateQueue();
    },
  });

  const onCloseAppointmentForm = () => {
    setIsNewAppointmentFormOpen(false);
    setSelectedAppointment(null);
  };

  return (
    <PageLayout
      title="Walk-in Queue"
      titleAction={{
        label: 'Add Walk-In',
        onClick: () => {
          if (!hasQueue) {
            openSubscriptionModal();
            return;
          }
          setIsNewAppointmentFormOpen(true);
        },
      }}
      guard={{
        isAllowed: !!user && userHasAccessToQueue(user),
      }}
      isLoading={isFetching && !isLoading}
      refresh={() => {
        refetchQueue();
      }}>
      {isPaused && !queue ? (
        <OfflineState
          onRetry={() => refetchQueue()}
          isRetrying={isRefetching}
        />
      ) : isLoading ? (
        <PageLoading />
      ) : isError ? (
        <QueueError onRetry={() => refetchQueue()} isRetrying={isRefetching} />
      ) : hasQueueData ? (
        <QueueTable
          setSelectedAppointment={setSelectedAppointment}
          setSelectedViewAppointmentId={setSelectedViewAppointmentId}
        />
      ) : (
        <EmptyQueue />
      )}
      <FullPageModal
        modalOpen={isNewAppointmentFormOpen || !!selectedAppointment}
        setModalOpen={onCloseAppointmentForm}
        handleClose={onCloseAppointmentForm}
        name={
          selectedAppointment
            ? selectedViewAppointmentId
              ? 'Edit Walk-In'
              : 'Assign Walk-In'
            : 'Add Walk-In'
        }>
        <AppointmentForm
          selectedAppointment={
            isNewAppointmentFormOpen
              ? undefined
              : selectedAppointment || undefined
          }
          onClose={onCloseAppointmentForm}
          appointmentType="WALK_IN"
        />
      </FullPageModal>
      {!!selectedViewAppointmentId && (
        <AppointmentSideBarModal
          appointmentId={selectedViewAppointmentId}
          isOpen
          onClose={() => setSelectedViewAppointmentId(null)}
          onEdit={appt => {
            setSelectedAppointment(appt);
          }}
          onRefetchAppointments={() => {
            refetchQueue();
          }}
        />
      )}
    </PageLayout>
  );
}
